import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CurrencyEur, CheckCircle, Clock, ArrowsCounterClockwise, FloppyDisk, PencilSimple, Sparkle } from '@phosphor-icons/react';
import AnimatedNumber from './AnimatedNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// How many days a payment-plan change should be highlighted as "gewijzigd".
const RECENT_CHANGE_WINDOW_DAYS = 7;

function formatDateDutch(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const months = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
        return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch {
        return '';
    }
}

function daysSince(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
}

export default function PaymentPlan({ student, totalLessons, lessonsUsed, isInstructor, onUpdate }) {
    const [editing, setEditing] = useState(false);
    const [total, setTotal] = useState(student.payment_total || '');
    const [installments, setInstallments] = useState(student.payment_installments || 4);
    const [paid, setPaid] = useState(student.paid_installments || []);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setTotal(student.payment_total || '');
        setInstallments(student.payment_installments || 4);
        setPaid(student.paid_installments || []);
    }, [student.id, student.payment_total, student.payment_installments, student.paid_installments]);

    const numInstallments = Number(installments) || 0;
    const totalAmount = Number(total) || 0;
    const installmentAmount = numInstallments > 0 ? totalAmount / numInstallments : 0;
    const lessonsPerInstallment = (numInstallments > 0 && totalLessons > 0)
        ? totalLessons / numInstallments
        : 0;

    // Determine current installment based on lessons used
    const currentInstallment = lessonsPerInstallment > 0
        ? Math.min(numInstallments, Math.floor(lessonsUsed / lessonsPerInstallment) + (lessonsUsed >= lessonsPerInstallment * numInstallments ? 0 : 0))
        : 0;
    // active = first unpaid that is "due" based on lessons given
    const activeIdx = (() => {
        for (let i = 0; i < numInstallments; i++) {
            if (paid.includes(i)) continue;
            // due if lessons used >= triggerPoint for this installment
            const triggerLessons = i * lessonsPerInstallment;
            if (lessonsUsed >= triggerLessons) return i;
        }
        return -1;
    })();

    const togglePaid = async (idx) => {
        if (!isInstructor) return;
        const next = paid.includes(idx) ? paid.filter(p => p !== idx) : [...paid, idx].sort((a, b) => a - b);
        setPaid(next);
        try {
            await axios.put(`${API}/students/${student.id}`, { paid_installments: next }, { withCredentials: true });
            if (onUpdate) onUpdate({ ...student, paid_installments: next });
        } catch (e) {
            console.error('toggle paid', e);
            setPaid(paid); // revert
        }
    };

    const saveSettings = async () => {
        if (saving) return;
        setSaving(true);
        try {
            // Trim paid to installments range
            const trimmedPaid = (paid || []).filter(p => p < numInstallments);
            await axios.put(`${API}/students/${student.id}`, {
                payment_total: totalAmount,
                payment_installments: numInstallments,
                paid_installments: trimmedPaid,
            }, { withCredentials: true });
            setPaid(trimmedPaid);
            if (onUpdate) {
                onUpdate({
                    ...student,
                    payment_total: totalAmount,
                    payment_installments: numInstallments,
                    paid_installments: trimmedPaid,
                });
            }
            setEditing(false);
        } catch (e) {
            console.error('save settings', e);
        } finally {
            setSaving(false);
        }
    };

    // No plan yet
    if (!student.payment_total && !editing && isInstructor) {
        return (
            <Card className="bg-[#2D3748] border-[#4A5568]" data-testid="payment-plan-card">
                <CardContent className="py-6 text-center">
                    <CurrencyEur size={36} className="mx-auto text-[#D4A537] mb-2" weight="fill" />
                    <h3 className="text-white font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Betaling in termijnen</h3>
                    <p className="text-sm text-gray-400 mb-3">Stel het pakketbedrag en aantal termijnen in.</p>
                    <Button
                        onClick={() => setEditing(true)}
                        className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                        data-testid="setup-payment-plan"
                    >
                        Termijnen instellen
                    </Button>
                </CardContent>
            </Card>
        );
    }

    // Student view, no plan -> hide
    if (!student.payment_total && !isInstructor) {
        return null;
    }

    return (
        <Card className="bg-[#2D3748] border-[#4A5568]" data-testid="payment-plan-card">
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <CardTitle className="text-white flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <CurrencyEur size={22} className="text-[#D4A537]" weight="fill" />
                        Betaling in termijnen
                        {(() => {
                            const d = daysSince(student.payment_updated_at);
                            if (d === null || d > RECENT_CHANGE_WINDOW_DAYS) return null;
                            const label = d === 0 ? 'vandaag' : d === 1 ? 'gisteren' : `${d} dagen geleden`;
                            return (
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4A537]/50 bg-[#D4A537]/15 text-[#E5B84A] text-[10px] font-semibold uppercase tracking-wider animate-pulse"
                                    data-testid="payment-plan-updated-badge"
                                    title={`Laatst gewijzigd op ${formatDateDutch(student.payment_updated_at)}`}
                                >
                                    <Sparkle size={11} weight="fill" />
                                    Gewijzigd {label}
                                </span>
                            );
                        })()}
                    </CardTitle>
                    {isInstructor && !editing && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(true)}
                            className="h-8 border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                            data-testid="edit-payment-plan"
                        >
                            <PencilSimple size={14} className="mr-1" />
                            Wijzigen
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {editing ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-gray-400 uppercase tracking-wider">Totaalbedrag (€)</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={total}
                                    onChange={(e) => setTotal(e.target.value)}
                                    placeholder="1800"
                                    className="bg-[#1A202C] border-[#4A5568] text-white"
                                    data-testid="payment-total-input"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-gray-400 uppercase tracking-wider">Aantal termijnen</Label>
                                <div className="grid grid-cols-5 gap-1">
                                    {[1, 2, 3, 4, 5].map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setInstallments(n)}
                                            className={`h-9 rounded-md border-2 font-bold text-sm transition-all ${
                                                Number(installments) === n
                                                    ? 'border-[#D4A537] bg-[#D4A537]/15 text-[#D4A537]'
                                                    : 'border-[#4A5568] bg-[#1A202C] text-gray-400 hover:border-[#D4A537]/50'
                                            }`}
                                            data-testid={`installments-${n}`}
                                        >
                                            {n}×
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {totalAmount > 0 && numInstallments > 0 && (
                            <div className="p-3 bg-[#D4A537]/5 border border-[#D4A537]/30 rounded-md text-sm text-gray-300">
                                <div className="flex items-center gap-2 text-[#D4A537] font-semibold mb-1">
                                    <ArrowsCounterClockwise size={16} weight="bold" />
                                    Berekening
                                </div>
                                <div>{numInstallments} × <span className="text-white font-semibold">€{installmentAmount.toFixed(2)}</span> = €{totalAmount.toFixed(2)}</div>
                                {totalLessons > 0 && (
                                    <div className="text-xs text-gray-400 mt-1">
                                        Trigger: na elke <span className="text-white font-semibold">{lessonsPerInstallment.toFixed(1)} lessen</span> volgt een nieuwe termijn.
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setEditing(false);
                                    setTotal(student.payment_total || '');
                                    setInstallments(student.payment_installments || 4);
                                }}
                                className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                                data-testid="cancel-payment-plan"
                            >
                                Annuleren
                            </Button>
                            <Button
                                onClick={saveSettings}
                                disabled={saving || !totalAmount || !numInstallments}
                                className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                                data-testid="save-payment-plan"
                            >
                                <FloppyDisk size={16} className="mr-1" />
                                {saving ? 'Opslaan...' : 'Opslaan'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Summary */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="bg-[#1A202C] rounded-md p-3 text-center border border-[#4A5568]">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Totaal</div>
                                <div className="text-lg font-bold text-white">
                                    <AnimatedNumber value={Math.round(totalAmount)} duration={1300} prefix="€" />
                                </div>
                            </div>
                            <div className="bg-[#1A202C] rounded-md p-3 text-center border border-[#4A5568]">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Per termijn</div>
                                <div className="text-lg font-bold text-[#D4A537]">
                                    <AnimatedNumber value={Math.round(installmentAmount)} duration={1300} prefix="€" />
                                </div>
                            </div>
                            <div className="bg-[#1A202C] rounded-md p-3 text-center border border-[#4A5568]">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Betaald</div>
                                <div className="text-lg font-bold text-emerald-400">
                                    <AnimatedNumber value={paid.length} duration={1100} />/{numInstallments}
                                </div>
                            </div>
                        </div>

                        {/* Installments timeline */}
                        <div className="space-y-2">
                            {Array.from({ length: numInstallments }, (_, i) => {
                                const isPaid = paid.includes(i);
                                const isActive = i === activeIdx;
                                const triggerAt = Math.round(i * lessonsPerInstallment);
                                let status = 'upcoming';
                                if (isPaid) status = 'paid';
                                else if (isActive) status = 'active';
                                else if (lessonsPerInstallment > 0 && lessonsUsed >= triggerAt && i < activeIdx) status = 'overdue';
                                const colors = {
                                    paid: 'border-emerald-500/40 bg-emerald-500/10',
                                    active: 'border-[#D4A537] bg-[#D4A537]/10',
                                    upcoming: 'border-[#4A5568] bg-[#1A202C]',
                                    overdue: 'border-red-500/40 bg-red-500/10',
                                };
                                return (
                                    <div
                                        key={i}
                                        className={`flex items-center gap-3 p-3 rounded-md border ${colors[status]}`}
                                        data-testid={`installment-row-${i}`}
                                    >
                                        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold ${
                                            isPaid ? 'bg-emerald-500 text-white' :
                                            isActive ? 'bg-[#D4A537] text-[#1A202C]' :
                                            status === 'overdue' ? 'bg-red-500 text-white' :
                                            'bg-[#4A5568] text-gray-300'
                                        }`}>
                                            {isPaid ? <CheckCircle size={20} weight="fill" /> : (i + 1)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-white">Termijn {i + 1} van {numInstallments}</span>
                                                {isPaid && <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-medium">Betaald</span>}
                                                {!isPaid && isActive && <span className="text-xs px-2 py-0.5 rounded bg-[#D4A537]/20 text-[#D4A537] font-medium">Aan de beurt</span>}
                                                {!isPaid && status === 'overdue' && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300 font-medium">Achterstallig</span>}
                                                {!isPaid && status === 'upcoming' && <span className="text-xs px-2 py-0.5 rounded bg-[#4A5568] text-gray-400 font-medium">Komt nog</span>}
                                            </div>
                                            <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-3">
                                                <span className="text-white font-semibold">€{installmentAmount.toFixed(2)}</span>
                                                {totalLessons > 0 && lessonsPerInstallment > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={12} />
                                                        Vanaf les {triggerAt + 1}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {isInstructor && (
                                            <Button
                                                variant={isPaid ? 'outline' : 'default'}
                                                size="sm"
                                                onClick={() => togglePaid(i)}
                                                className={isPaid
                                                    ? 'border-[#4A5568] text-gray-400 hover:bg-[#3D4A5C]'
                                                    : 'bg-emerald-600 hover:bg-emerald-500 text-white font-semibold'}
                                                data-testid={`toggle-paid-${i}`}
                                            >
                                                {isPaid ? 'Ongedaan' : 'Markeer betaald'}
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {!isInstructor && activeIdx >= 0 && (
                            <div className="p-3 bg-[#D4A537]/5 border border-[#D4A537]/30 rounded-md text-sm text-gray-300">
                                <strong className="text-[#D4A537]">Volgende termijn aan de beurt:</strong> Termijn {activeIdx + 1} (€{installmentAmount.toFixed(2)}). Stem af met je rijinstructeur.
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
